import { motion } from "motion/react";
import Link from "@/components/compat/Link";
import { LogoIcon } from "@/components/ui/logo-icon";
import { Logotype } from "@/components/ui/logotype";
import { SpacetimeGrid } from "@/components/ui/spacetime-grid";
import { LocaleToggleUnauth } from "./LocaleToggleUnauth";
import { useMarketingNav } from "./MarketingNavProvider";

const MotionLink = motion.create(Link);

interface UnauthShellProps {
  children: React.ReactNode;
  /** "center" vertically centers content, "scroll" allows scrolling with a sticky header (for long pages like terms) */
  mode?: "center" | "scroll";
  /** Remove the shared marketing header for self-contained conversion pages. */
  hideHeader?: boolean;
  /** Hide the centered logotype (e.g. login has its own hero logotype in the body). The left LogoIcon stays visible as the nav-drawer trigger. */
  hideLogotype?: boolean;
  /** Reduce the animated canvas cost on mobile conversion pages. */
  optimizeSpacetimeGridForMobile?: boolean;
  /** Lower background resolution and frame rate on pages with interactive graphics. */
  lowPower?: boolean;
}

const EASE = [0.16, 1, 0.3, 1] as const;

const MARKETING_NAV_V2 = import.meta.env.VITE_MARKETING_NAV_V2 === "true";

function UnauthHeader({
  hideLogotype = false,
  lowPower = false,
}: {
  hideLogotype?: boolean;
  lowPower?: boolean;
}) {
  const { openNav } = useMarketingNav();
  return (
    <>
      {MARKETING_NAV_V2 ? (
        <motion.button
          animate={{ opacity: 1, scale: 1 }}
          aria-label="Open menu"
          className="flex cursor-pointer items-center"
          initial={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.4, ease: EASE, delay: 0.1 }}
          type="button"
          onClick={openNav}
        >
          <LogoIcon foil foilFps={lowPower ? 24 : undefined} size="sm" />
        </motion.button>
      ) : hideLogotype ? (
        <div />
      ) : (
        <MotionLink
          animate={{ opacity: 1, scale: 1 }}
          className="flex items-center"
          href="/login"
          initial={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.4, ease: EASE, delay: 0.1 }}
        >
          <LogoIcon foil foilFps={lowPower ? 24 : undefined} size="sm" />
        </MotionLink>
      )}
      {hideLogotype ? (
        <div />
      ) : (
        <MotionLink
          animate={{ opacity: 1, y: 0 }}
          className="justify-self-center"
          href="/login"
          initial={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.5, ease: EASE, delay: 0.15 }}
        >
          <Logotype size="md" />
        </MotionLink>
      )}
      <div className="flex items-center justify-end">
        <LocaleToggleUnauth />
      </div>
    </>
  );
}

export function UnauthShell({
  children,
  mode = "center",
  hideHeader = false,
  hideLogotype = false,
  optimizeSpacetimeGridForMobile = false,
  lowPower = false,
}: UnauthShellProps) {
  if (mode === "scroll") {
    return (
      <div className="relative flex-1 pt-[env(safe-area-inset-top)]">
        <SpacetimeGrid
          optimizeForMobile={optimizeSpacetimeGridForMobile}
          lowPower={lowPower}
        />
        {hideHeader ? null : (
          <header className="sticky top-0 z-20 mx-auto grid w-full max-w-2xl grid-cols-[1fr_auto_1fr] items-center px-4 py-3">
            <UnauthHeader hideLogotype={hideLogotype} lowPower={lowPower} />
          </header>
        )}
        {children}
      </div>
    );
  }

  return (
    <div className="relative flex flex-1 overflow-hidden pt-[env(safe-area-inset-top)]">
      <SpacetimeGrid
        optimizeForMobile={optimizeSpacetimeGridForMobile}
        lowPower={lowPower}
      />
      {hideHeader ? null : (
        <div className="absolute inset-x-0 top-0 z-20">
          <header className="mx-auto grid w-full max-w-2xl grid-cols-[1fr_auto_1fr] items-center px-4 py-3">
            <UnauthHeader hideLogotype={hideLogotype} lowPower={lowPower} />
          </header>
        </div>
      )}
      <main className="relative flex flex-1 items-center justify-center overflow-hidden px-4">
        {children}
      </main>
    </div>
  );
}
